import { apiGet, apiPost, apiPut, apiDelete, apiRequest } from '@/utils/request';
import { parseListResponse, parsePaginatedResponse } from '@/utils/apiResponse';
import type {
  TencentAdAccountsData,
  ServiceUnitItem,
  OrganizationItem,
  BindableAccountItem,
  BindingItem,
  TencentAdTransItem,
  SwitchType,
} from '@/types/adReporting';
import type { ClueConfig } from '@/types/customerReporting';

export const tencentAdApi = {
  /* ---- 账户管理 ---- */

  /** 单账户列表（含绑定开关） */
  accounts: (params?: Record<string, unknown>) =>
    apiGet<TencentAdAccountsData>('/tencent-ad/accounts', params),

  /** 停用/启用广告账户 */
  changeAccountStatus: (data: { id: number; status: 0 | 1 }) =>
    apiPost('/tencent-ad/accounts/change-status', data),

  /** 切换账户级上报总开关 */
  changeAccountReport: (data: { id: number; report_enabled: 0 | 1 }) =>
    apiPost('/tencent-ad/accounts/change-report', data),

  /* ---- 服务单元 ---- */

  /** 服务单元及其子账户 */
  serviceUnits: () =>
    apiGet('/tencent-ad/accounts/service-units').then((d) => parseListResponse<ServiceUnitItem>(d)),

  /** 服务单元下属广告主（腾讯API） */
  organization: (params: { id: number; page?: number; page_size?: number }) =>
    apiGet<{ list: OrganizationItem[]; page_info: Record<string, number> }>(
      '/tencent-ad/accounts/organization',
      params as Record<string, unknown>,
    ),

  /** 从服务单元添加子账户 */
  addChildren: (data: {
    parent_id: number;
    accounts: { account_id: number; corporation_name: string }[];
  }) => apiPost('/tencent-ad/accounts/add-children', data),

  /* ---- 绑定管理 ---- */

  /** 可绑定的渠道账号列表 */
  bindableAccounts: (params?: Record<string, unknown>) =>
    apiGet('/tencent-ad/bindable-accounts', params).then((d) =>
      parseListResponse<BindableAccountItem>(d),
    ),

  /** 绑定关系列表 */
  bindings: (params?: Record<string, unknown>) =>
    apiGet('/tencent-ad/bindings', params).then((d) => parseListResponse<BindingItem>(d)),

  /** 创建绑定 */
  createBinding: (data: { tencent_ad_account_id: number; account_ids: number[] }) =>
    apiPost('/tencent-ad/bindings', data),

  /** 更新绑定的渠道账号 */
  updateBindingAccounts: (data: { id: number; account_ids: number[] }) =>
    apiPut('/tencent-ad/bindings/accounts', data),

  /** 解除绑定 */
  deleteBinding: (id: number) => apiRequest(`/tencent-ad/bindings/${id}`, { method: 'DELETE' }),

  /** 切换上报开关 */
  changeSwitch: (data: { id: number; type: SwitchType; value: 0 | 1 }) =>
    apiPost('/tencent-ad/bindings/change-switch', data),

  /* ---- 上报记录 ---- */

  /** 上报记录列表（分页） */
  trans: (params?: Record<string, unknown>) =>
    apiGet('/tencent-ad/trans', params).then((d) => parsePaginatedResponse<TencentAdTransItem>(d)),

  /** 手动重新上报 */
  report: (data: { id: number }) => apiPost('/tencent-ad/report', data),

  /* ---- 客资回传配置 ---- */

  /** 客资配置可绑定的渠道账号列表 */
  clueConfigBindableAccounts: (params: {
    channel_key: string;
    config_id?: number;
    keyword?: string;
  }) =>
    apiGet('/tencent-ad/clue-configs/bindable-accounts', params as Record<string, unknown>).then(
      (d) => parseListResponse<{ id: number; name: string; alias: string }>(d),
    ),

  /** 客资回传配置列表（分页） */
  clueConfigs: (params?: {
    channel_key?: string;
    status?: number;
    name?: string;
    page?: number;
    per_page?: number;
  }) =>
    apiGet('/tencent-ad/clue-configs', params as Record<string, unknown>).then((d) =>
      parsePaginatedResponse<ClueConfig>(d),
    ),

  /** 创建客资回传配置 */
  createClueConfig: (data: {
    name: string;
    channel_key: string;
    trigger_condition: number;
    account_ids: number[];
    enabled?: number;
  }) => apiPost('/tencent-ad/clue-configs', data),

  /** 更新客资回传配置 */
  updateClueConfig: (
    id: number,
    data: {
      name: string;
      channel_key: string;
      trigger_condition: number;
      account_ids: number[];
      enabled?: number;
    },
  ) => apiPut(`/tencent-ad/clue-configs/${id}`, data),

  /** 删除客资回传配置 */
  deleteClueConfig: (id: number) => apiDelete(`/tencent-ad/clue-configs/${id}`),

  /** 启用/停用客资回传配置 */
  toggleClueConfig: (data: { id: number; enabled: number }) =>
    apiPost('/tencent-ad/clue-configs/toggle', data),
};
